import { cn } from '@/lib/utils';

/**
 * The Getting Started progress ring.
 *
 * One component, three call sites: the sidebar widget (16px), the icon rail
 * (18px) and the hub's own heading (22px). Each of the three used to carry a
 * byte-for-byte copy of this SVG differing only in the default `size` and
 * `strokeWidth`, which is three places to fix a ring that draws the wrong
 * fraction — and they already disagreed about the number they were drawing
 * (VALE-1830).
 *
 * `aria-hidden`, because every call site labels itself in text beside the ring
 * ("Getting Started", "2 / 6 steps"). A ring announcing a percentage as well
 * would read the same fact twice.
 */
export function GettingStartedProgressRing({
  completed,
  total,
  size = 20,
  className
}: {
  completed: number;
  total: number;
  size?: number;
  className?: string;
}) {
  // Scaled from the size so a 16px ring is not drawn with a 22px ring's stroke.
  const strokeWidth = size >= 22 ? 2.5 : 2;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = total > 0 ? Math.min(completed / total, 1) : 0;
  const offset = circumference - progress * circumference;

  return (
    <svg
      width={size}
      height={size}
      className={cn('shrink-0 -rotate-90', className)}
      aria-hidden="true"
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        className="text-foreground-subtle"
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        className="text-foreground transition-all duration-500"
      />
    </svg>
  );
}
